import { FormEvent, useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, Navigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { annualLeaveAvailableForRequest, annualLeaveWeeksOf, annualRequestDays, annualRequestExceedsAccrual, annualShortfallMessage, canCancelRequest, contractualAnnualDays, dailyHoursOf, dayEquivalentsPerWeek, DEFAULT_HOURS_PER_DAY, fullDayHours, normalizeWorkingDays, workingPatternFromInput } from './accrual'
import { eachDate, formatDisplayDate, monthBounds, parseIsoDate, weekday } from './dates'
import { LEAVE_SESSION_STORAGE_KEY, leaveService } from './leaveService'
import { publicHolidayOn, shiftMonth } from './payCycle'
import { generatePin } from './pin'
import { leaveToday } from './productModule'
import type { AdditionalHours, DayPortion, LeaveBalance, LeaveCalendarEntry, LeaveEmail, LeaveEmployee, LeaveEmployeeInput, LeaveEmployeeWithBalance, LeaveRequest, LeaveStatus, LeaveType, PayReport, PayReportLine } from './types'
import { useLeaveModuleEnabled } from './useLeaveModuleEnabled'
import './TeamPage.css'

function messageFrom(error: unknown): string {
  if (error && typeof error === 'object' && 'response' in error) {
    const message = (error as { response?: { data?: { message?: string } } }).response?.data?.message
    if (message) return message
  }
  return error instanceof Error ? error.message : 'Something went wrong. Please try again.'
}

function requestStatusLabel(status: LeaveStatus): string {
  if (status === 'pending') return 'Waiting for approval'
  if (status === 'approved') return 'Approved'
  if (status === 'declined') return 'Declined'
  return 'Cancelled'
}

function requestDateLabel(request: Pick<LeaveRequest, 'startDate' | 'endDate'>): string {
  if (request.startDate === request.endDate) return formatDisplayDate(request.startDate)
  return `${formatDisplayDate(request.startDate)} – ${formatDisplayDate(request.endDate)}`
}

function formatDays(value: number): string {
  return (Math.round(value * 100) / 100).toFixed(2)
}

function annualRequestWarning(
  employee: LeaveEmployee | undefined,
  requests: LeaveRequest[],
  draft: Pick<LeaveRequest, 'type' | 'startDate' | 'endDate' | 'startPortion' | 'endPortion'> & { id?: string },
): string {
  if (!employee || draft.type !== 'annual' || !draft.startDate || !draft.endDate || draft.endDate < draft.startDate) return ''
  try {
    if (!annualRequestExceedsAccrual(employee, requests, draft)) return ''
    return annualShortfallMessage(
      annualRequestDays(employee, draft),
      annualLeaveAvailableForRequest(employee, requests, draft),
    )
  } catch {
    return ''
  }
}

const WEEKDAYS = [
  { value: 1, short: 'Mon', name: 'Monday' },
  { value: 2, short: 'Tue', name: 'Tuesday' },
  { value: 3, short: 'Wed', name: 'Wednesday' },
  { value: 4, short: 'Thu', name: 'Thursday' },
  { value: 5, short: 'Fri', name: 'Friday' },
  { value: 6, short: 'Sat', name: 'Saturday' },
  { value: 0, short: 'Sun', name: 'Sunday' },
]

function workingDayNames(days: number[]): string {
  const selected = WEEKDAYS.filter((day) => days.includes(day.value))
  const mondayToFriday = selected.length === 5 && [1, 2, 3, 4, 5].every((day) => days.includes(day))
  return mondayToFriday ? 'Monday to Friday' : selected.map((day) => day.short).join(', ')
}

function formatDayHours(value: number): string {
  return String(Math.round(value * 100) / 100)
}

const DEFAULT_DAILY_HOURS: Record<string, number> = {
  1: DEFAULT_HOURS_PER_DAY,
  2: DEFAULT_HOURS_PER_DAY,
  3: DEFAULT_HOURS_PER_DAY,
  4: DEFAULT_HOURS_PER_DAY,
  5: DEFAULT_HOURS_PER_DAY,
}

function worksHoursLabel(employee: LeaveEmployee): string {
  const daily = dailyHoursOf(employee)
  const ordered = WEEKDAYS.map((day) => daily[String(day.value)]).filter((hours): hours is number => typeof hours === 'number')
  if (!ordered.length) return ''
  if (ordered.every((hours) => hours === ordered[0])) return `${formatDayHours(ordered[0])}h / day`
  return `${ordered.map((hours) => formatDayHours(hours)).join(' · ')}h`
}

function dailyHoursFromForm(form: FormData): Record<string, string> {
  const dailyHours: Record<string, string> = {}
  WEEKDAYS.forEach((day) => {
    dailyHours[String(day.value)] = String(form.get(`dailyHour-${day.value}`) ?? '')
  })
  return dailyHours
}

function hoursForDay(dailyHours: Record<string, number>, day: number): string {
  const hours = dailyHours[String(day)]
  return typeof hours === 'number' && hours > 0 ? formatDayHours(hours) : '0'
}

function entitlementText(weeksValue: string, hoursByDay: Record<string, string>): string {
  const weeks = Number(weeksValue)
  if (!Number.isFinite(weeks) || weeks < 0) return 'Enter the contractual weeks.'
  try {
    const dailyHours: Record<string, number> = {}
    Object.entries(hoursByDay).forEach(([key, value]) => {
      const hours = Number(value)
      if (Number.isFinite(hours)) dailyHours[key] = hours
    })
    const pattern = workingPatternFromInput({ dailyHours })
    const shaped = { ...pattern, annualLeaveWeeks: weeks }
    return `${formatDays(contractualAnnualDays(shaped))} days a year. ${formatDays(weeks)} weeks × ${formatDays(dayEquivalentsPerWeek(shaped))} days. A full day is ${formatHours(fullDayHours(shaped))} hours, so a shorter day counts as part of a day.`
  } catch {
    return 'Choose at least one working day.'
  }
}

function WorkingPatternFields({ idPrefix, dailyHours, weeks }: { idPrefix: string; dailyHours: Record<string, number>; weeks: string }) {
  const [hoursByDay, setHoursByDay] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {}
    WEEKDAYS.forEach((day) => {
      initial[String(day.value)] = hoursForDay(dailyHours, day.value)
    })
    return initial
  })
  const weekly = WEEKDAYS.reduce((sum, day) => {
    const hours = Number(hoursByDay[String(day.value)])
    return Number.isFinite(hours) ? sum + hours : sum
  }, 0)
  return (
    <fieldset className="working-pattern">
      <legend>Hours each working day</legend>
      <div className="day-hours" role="group" aria-label="Hours each working day">
        {WEEKDAYS.map((day) => {
          const key = String(day.value)
          const hours = Number(hoursByDay[key])
          const on = Number.isFinite(hours) && hours > 0
          return (
            <div key={day.value} className={on ? 'day-hour is-on' : 'day-hour'}>
              <label className={on ? 'day-toggle is-on' : 'day-toggle'}>
                <input
                  type="checkbox"
                  checked={on}
                  aria-label={day.name}
                  onChange={() => {
                    setHoursByDay((current) => {
                      const currentHours = Number(current[key])
                      const next = Number.isFinite(currentHours) && currentHours > 0 ? '0' : String(DEFAULT_HOURS_PER_DAY)
                      return { ...current, [key]: next }
                    })
                  }}
                />
                <span>{day.short}</span>
              </label>
              <input
                id={`${idPrefix}-hours-${day.value}`}
                name={`dailyHour-${day.value}`}
                type="number"
                inputMode="decimal"
                min="0"
                max="24"
                step="0.1"
                value={hoursByDay[key] ?? '0'}
                aria-label={`Hours on ${day.name}`}
                onChange={(event) => setHoursByDay((current) => ({ ...current, [key]: event.target.value }))}
              />
            </div>
          )
        })}
      </div>
      <p className="working-summary">{formatDayHours(weekly)} hours a week. {entitlementText(weeks, hoursByDay)} Change 0 to the hours they work, or turn the day on. A new day starts at 7.5 hours.</p>
    </fieldset>
  )
}

function formatHours(value: number): string {
  return (Math.round(value * 10) / 10).toFixed(1)
}

function leaveTypeLabel(type: LeaveType): string {
  if (type === 'sick') return 'Sick leave'
  if (type === 'unpaid') return 'Unpaid leave'
  if (type === 'study') return 'Study leave'
  return 'Annual leave'
}

function leaveDayClass(type: LeaveType): string {
  if (type === 'sick') return 'leave-day-sick'
  if (type === 'unpaid') return 'leave-day-unpaid'
  if (type === 'study') return 'leave-day-study'
  return 'leave-day-annual'
}

function calendarMark(type: LeaveType, portion: DayPortion): string {
  if (portion === 0.5) return '½'
  if (type === 'sick') return 'S'
  if (type === 'unpaid') return 'L'
  if (type === 'study') return 'St'
  return 'A'
}

function calendarPhrase(type: LeaveType): string {
  if (type === 'sick') return 'sick leave'
  if (type === 'unpaid') return 'leave'
  if (type === 'study') return 'study leave'
  return 'annual leave'
}

function formLeaveType(value: string): LeaveType {
  if (value === 'sick' || value === 'unpaid' || value === 'study') return value
  return 'annual'
}

function formatLeaveRange(line: Pick<PayReportLine, 'startDate' | 'endDate'>): string {
  if (line.startDate === line.endDate) return formatDisplayDate(line.startDate)
  return `${formatDisplayDate(line.startDate)} – ${formatDisplayDate(line.endDate)}`
}

function PayReportDocument({
  payday,
  periodStart,
  periodEnd,
  lines,
  additionalHours = [],
}: {
  payday: string
  periodStart: string
  periodEnd: string
  lines: PayReportLine[]
  additionalHours?: AdditionalHours[]
}) {
  const rows = [...lines].sort((a, b) => a.employeeName.localeCompare(b.employeeName) || a.startDate.localeCompare(b.startDate))
  const totalDays = rows.reduce((sum, line) => sum + line.days, 0)
  const totalHours = rows.reduce((sum, line) => sum + line.hours, 0)

  return (
    <article className="pay-report">
      <header className="pay-report-header">
        <h3>Leave report</h3>
        <dl className="pay-report-meta">
          <div>
            <dt>Pay date</dt>
            <dd>{formatDisplayDate(payday)}</dd>
          </div>
          <div>
            <dt>Period</dt>
            <dd>{formatDisplayDate(periodStart)} – {formatDisplayDate(periodEnd)}</dd>
          </div>
        </dl>
      </header>
      {rows.length === 0 ? (
        <p className="pay-report-empty">No approved leave falls in this pay period.</p>
      ) : (
        <div className="pay-report-scroll">
          <table>
            <caption>Approved leave for this pay period</caption>
            <thead>
              <tr>
                <th scope="col">Employee</th>
                <th scope="col">Type</th>
                <th scope="col">Dates</th>
                <th scope="col" className="num">Days</th>
                <th scope="col" className="num">Hours</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((line) => (
                <tr key={`${line.employeeId}-${line.startDate}-${line.type}`}>
                  <th scope="row">{line.employeeName}</th>
                  <td>{leaveTypeLabel(line.type)}</td>
                  <td>{formatLeaveRange(line)}</td>
                  <td className="num">{formatDays(line.days)}</td>
                  <td className="num">{formatHours(line.hours)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <th scope="row" colSpan={3}>Total</th>
                <td className="num">{formatDays(totalDays)}</td>
                <td className="num">{formatHours(totalHours)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
      <h4 className="pay-report-section">Additional hours worked</h4>
      {additionalHours.length === 0 ? (
        <p className="pay-report-empty">No additional hours in this pay period.</p>
      ) : (
        <div className="pay-report-scroll">
          <table>
            <caption>Additional hours worked in this pay period</caption>
            <thead>
              <tr>
                <th scope="col">Employee</th>
                <th scope="col">Date</th>
                <th scope="col" className="num">Hours</th>
                <th scope="col">Note</th>
              </tr>
            </thead>
            <tbody>
              {additionalHours.map((item) => (
                <tr key={item.id}>
                  <th scope="row">{item.employeeName}</th>
                  <td>{formatDisplayDate(item.date)}</td>
                  <td className="num">{formatHours(item.hours)}</td>
                  <td>{item.note || ''}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <th scope="row" colSpan={2}>Total additional hours</th>
                <td className="num">{formatHours(additionalHours.reduce((sum, item) => sum + item.hours, 0))}</td>
                <td />
              </tr>
            </tfoot>
          </table>
        </div>
      )}
      <p className="pay-report-note">Approved leave only. Hours are the employee’s regular weekly hours divided by five for each leave day. Additional hours are extra time worked and are listed separately.</p>
    </article>
  )
}

function monthLabel(month: string): string {
  return parseIsoDate(`${month}-01`).toLocaleDateString('en-IE', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  })
}

function LeaveCalendar() {
  const [month, setMonth] = useState(() => leaveToday().slice(0, 7))
  const calendar = useQuery({
    queryKey: ['leave-calendar', month],
    queryFn: () => leaveService.getCalendar(month),
  })
  const bounds = monthBounds(month)
  const days = eachDate(bounds.start, bounds.end)
  const entries = calendar.data?.entries || []
  const entryFor = (employeeId: string, date: string): LeaveCalendarEntry | undefined => (
    entries.find((entry) => entry.employeeId === employeeId && entry.date === date)
  )

  return (
    <section className="team-panel" aria-labelledby="leave-calendar-heading">
      <div className="team-calendar-toolbar">
        <h2 id="leave-calendar-heading">Leave calendar</h2>
        <div className="team-actions">
          <button type="button" onClick={() => setMonth((current) => shiftMonth(current, -1))}>Previous month</button>
          <button type="button" onClick={() => setMonth((current) => shiftMonth(current, 1))}>Next month</button>
        </div>
      </div>
      <p className="team-muted">{monthLabel(month)}. Approved leave for the whole team. Public holidays are not counted as leave.</p>
      <p className="team-legend">
        <span><i className="swatch swatch-annual" aria-hidden="true" /> Annual</span>
        <span><i className="swatch swatch-sick" aria-hidden="true" /> Sick</span>
        <span><i className="swatch swatch-study" aria-hidden="true" /> Study</span>
        <span><i className="swatch swatch-leave" aria-hidden="true" /> Leave</span>
        <span><i className="swatch swatch-half" aria-hidden="true" /> Half day</span>
        <span><i className="swatch swatch-holiday" aria-hidden="true" /> Public holiday</span>
        <a
          href="https://www.citizensinformation.ie/en/employment/employment-rights-and-conditions/leave-and-holidays/public-holidays/"
          target="_blank"
          rel="noopener noreferrer"
        >
          Public holidays in Ireland
        </a>
      </p>
      {calendar.isLoading && <p>Loading the calendar…</p>}
      {calendar.error && <p className="team-error" role="alert">{messageFrom(calendar.error)}</p>}
      {calendar.data && (
        <div className="leave-calendar-scroll">
          <table className="leave-calendar">
            <caption className="team-muted">Who is on leave in {monthLabel(month)}</caption>
            <thead>
              <tr>
                <th className="employee-col" scope="col">Employee</th>
                {days.map((date) => {
                  const day = Number(date.slice(8, 10))
                  const weekend = weekday(date) === 0 || weekday(date) === 6
                  const today = date === leaveToday()
                  const holiday = publicHolidayOn(date)
                  const title = [
                    formatDisplayDate(date),
                    holiday ? holiday.name : '',
                    today ? 'today' : '',
                  ].filter(Boolean).join(', ')
                  return (
                    <th key={date} scope="col" className={[weekend ? 'weekend' : '', holiday ? 'is-public-holiday' : '', today ? 'is-today' : ''].filter(Boolean).join(' ') || undefined} title={title}>
                      {day}
                    </th>
                  )
                })}
              </tr>
            </thead>
            <tbody>
              {calendar.data.employees.map((employee) => (
                <tr key={employee.id}>
                  <th className="employee-name" scope="row">{employee.name}</th>
                  {days.map((date) => {
                    const entry = entryFor(employee.id, date)
                    const weekend = weekday(date) === 0 || weekday(date) === 6
                    const today = date === leaveToday()
                    const holiday = publicHolidayOn(date)
                    const label = entry
                      ? `${employee.name} is on ${calendarPhrase(entry.type)} on ${formatDisplayDate(date)}${entry.portion === 0.5 ? ', half day' : ''}${holiday ? `, ${holiday.name}` : ''}`
                      : holiday
                        ? `${holiday.name} on ${formatDisplayDate(date)}. ${employee.name} is not on leave.`
                        : `${employee.name} is working on ${formatDisplayDate(date)}`
                    const className = [
                      weekend && !entry ? 'weekend' : '',
                      holiday ? 'is-public-holiday' : '',
                      today ? 'is-today' : '',
                      entry ? leaveDayClass(entry.type) : '',
                      entry?.portion === 0.5 ? 'leave-day-half' : '',
                    ].filter(Boolean).join(' ')
                    return (
                      <td key={date} className={className || undefined} aria-label={label}>
                        {entry ? calendarMark(entry.type, entry.portion) : ''}
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}

function LeaveBalanceSummary({ balance }: { balance: LeaveBalance }) {
  return (
    <section className="leave-balance" aria-label={`Leave balance as of ${formatDisplayDate(balance.asOf)}`}>
      <p className="leave-balance-asof">As of {formatDisplayDate(balance.asOf)}</p>
      <dl className="leave-balance-grid">
        <div className="leave-balance-card is-annual">
          <dt>Annual leave</dt>
          <dd>
            <span className="leave-balance-value">{formatDays(balance.annualAvailable)}</span>
            <span className="leave-balance-unit">days available</span>
          </dd>
        </div>
        <div className="leave-balance-card is-accrued">
          <dt>Accrued</dt>
          <dd>
            <span className="leave-balance-value">{formatDays(balance.annualAccrued)}</span>
            <span className="leave-balance-unit">days earned this year</span>
            <span className="leave-balance-detail">Taken {formatDays(balance.annualTaken)} · Booked {formatDays(balance.annualBooked)}</span>
            {balance.annualAccrualFrozen && <span className="leave-balance-detail">No further annual leave accrues until 1 January. The available balance is what remains this year.</span>}
            {balance.annualBalanceSet && !balance.annualAccrualFrozen && <span className="leave-balance-detail">Started from a balance set by an admin. It still increases through this year.</span>}
          </dd>
        </div>
        <div className="leave-balance-card is-sick">
          <dt>Sick leave</dt>
          <dd>
            {balance.sickEligible ? (
              <>
                <span className="leave-balance-value">{formatDays(balance.sickAvailable)}</span>
                <span className="leave-balance-unit">days remaining</span>
                <span className="leave-balance-detail">
                  {balance.sickBalanceSet
                    ? 'Balance set by an admin'
                    : `${formatDays(balance.sickUsed)} used of ${formatDays(balance.sickEntitlement)} statutory days`}
                </span>
              </>
            ) : (
              <>
                <span className="leave-balance-value">Not yet</span>
                <span className="leave-balance-unit">statutory sick leave</span>
                <span className="leave-balance-detail">Available from {formatDisplayDate(balance.sickEligibleFrom)}</span>
              </>
            )}
          </dd>
        </div>
        <div className="leave-balance-card is-study">
          <dt>Study leave</dt>
          <dd>
            {balance.asOf < balance.studyYearStart ? (
              <>
                <span className="leave-balance-value">Not yet</span>
                <span className="leave-balance-unit">study leave</span>
                <span className="leave-balance-detail">Available from {formatDisplayDate(balance.studyYearStart)}</span>
              </>
            ) : (
              <>
                <span className="leave-balance-value">{formatDays(balance.studyAvailable)}</span>
                <span className="leave-balance-unit">days remaining</span>
                <span className="leave-balance-detail">
                  {formatDays(balance.studyUsed)} used of {formatDays(balance.studyEntitlement)} for {formatDisplayDate(balance.studyYearStart)} – {formatDisplayDate(balance.studyYearEnd)}. Unused days do not carry over.
                </span>
              </>
            )}
          </dd>
        </div>
      </dl>
    </section>
  )
}

function MyLeave() {
  const queryClient = useQueryClient()
  const [pin, setPin] = useState('')
  const [signedIn, setSignedIn] = useState(() => {
    try {
      return !!sessionStorage.getItem(LEAVE_SESSION_STORAGE_KEY)
    } catch {
      return false
    }
  })
  const [formError, setFormError] = useState('')
  const [notice, setNotice] = useState('')
  const [nextPin, setNextPin] = useState('')
  const [requestType, setRequestType] = useState<LeaveType>('annual')
  const [requestStart, setRequestStart] = useState('')
  const [requestEnd, setRequestEnd] = useState('')
  const [requestStartHalf, setRequestStartHalf] = useState(false)
  const [requestEndHalf, setRequestEndHalf] = useState(false)
  const me = useQuery({
    queryKey: ['leave-me'],
    queryFn: () => leaveService.getMe(),
    enabled: signedIn,
    retry: false,
  })

  useEffect(() => {
    if (!me.isError) return
    setSignedIn(false)
    try {
      sessionStorage.removeItem(LEAVE_SESSION_STORAGE_KEY)
    } catch {
      // sessionStorage can be unavailable
    }
  }, [me.isError])

  const signIn = useMutation({
    mutationFn: () => leaveService.signIn(pin),
    onSuccess: async () => {
      setSignedIn(true)
      setFormError('')
      setPin('')
      await queryClient.invalidateQueries({ queryKey: ['leave-me'] })
    },
    onError: (error) => setFormError(messageFrom(error)),
  })

  const signOut = useMutation({
    mutationFn: () => leaveService.signOut(),
    onSuccess: async () => {
      setSignedIn(false)
      setNotice('')
      setNextPin('')
      await queryClient.removeQueries({ queryKey: ['leave-me'] })
    },
  })

  const requestLeave = useMutation({
    mutationFn: leaveService.createRequest,
    onSuccess: async () => {
      setFormError('')
      setNotice('Request submitted. An admin will review it.')
      setRequestStart('')
      setRequestEnd('')
      setRequestStartHalf(false)
      setRequestEndHalf(false)
      await queryClient.invalidateQueries({ queryKey: ['leave-me'] })
      await queryClient.invalidateQueries({ queryKey: ['leave-requests'] })
    },
    onError: (error) => setFormError(messageFrom(error)),
  })

  const cancelRequest = useMutation({
    mutationFn: leaveService.cancelRequest,
    onSuccess: async () => {
      setFormError('')
      setNotice('Request cancelled.')
      await queryClient.invalidateQueries({ queryKey: ['leave-me'] })
      await queryClient.invalidateQueries({ queryKey: ['leave-requests'] })
      await queryClient.invalidateQueries({ queryKey: ['leave-calendar'] })
      await queryClient.invalidateQueries({ queryKey: ['leave-pay-report'] })
      await queryClient.invalidateQueries({ queryKey: ['leave-employees'] })
    },
    onError: (error) => setFormError(messageFrom(error)),
  })

  const changePin = useMutation({
    mutationFn: () => leaveService.changePin(nextPin),
    onSuccess: () => {
      setFormError('')
      setNotice(`Your PIN is now ${nextPin}. Use it the next time you sign in.`)
      setNextPin('')
    },
    onError: (error) => setFormError(messageFrom(error)),
  })

  const emailBalance = useMutation({
    mutationFn: () => leaveService.sendBalanceEmail(),
    onSuccess: (result) => setNotice(`Leave balance sent to ${result.sentTo}.`),
    onError: (error) => setFormError(messageFrom(error)),
  })

  const requestDraft = {
    id: 'draft',
    type: requestType,
    startDate: requestStart,
    endDate: requestEnd,
    startPortion: (requestStartHalf ? 0.5 : 1) as DayPortion,
    endPortion: (requestStart === requestEnd ? requestStartHalf : requestEndHalf) ? 0.5 as DayPortion : 1 as DayPortion,
  }
  const requestWarning = annualRequestWarning(me.data?.employee, me.data?.requests || [], requestDraft)

  const onRequest = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (requestWarning) {
      setFormError(requestWarning)
      return
    }
    const form = new FormData(event.currentTarget)
    requestLeave.mutate({
      type: requestType,
      startDate: requestStart,
      endDate: requestEnd,
      startPortion: requestDraft.startPortion,
      endPortion: requestDraft.endPortion,
      note: String(form.get('note') || ''),
    })
  }

  return (
    <section className="team-panel my-leave" aria-labelledby="my-leave-heading">
      <h2 id="my-leave-heading">My leave</h2>
      {!signedIn && (
        <form className="team-form" onSubmit={(event) => { event.preventDefault(); signIn.mutate() }}>
          <p className="team-note">Enter the 6-digit PIN for your employee record to see your balance or request leave. The calendar above is visible without a PIN.</p>
          {window.location.hostname === 'localhost' && (
            <p className="team-muted">Initial local demo PINs: Rachel 111111, Jason 222222, Aoife Kelly 333333, Liam Byrne 444444.</p>
          )}
          <label htmlFor="leave-pin">
            6-digit PIN
            <input
              id="leave-pin"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              pattern="\d{6}"
              value={pin}
              onChange={(event) => setPin(event.target.value.replace(/\D/g, '').slice(0, 6))}
              aria-required="true"
            />
          </label>
          {formError && <p className="team-error" role="alert">{formError}</p>}
          <button className="primary" type="submit">Continue</button>
        </form>
      )}
      {signedIn && me.data && (
        <div>
          <p className="leave-balance-name"><strong>{me.data.employee.name}</strong></p>
          <LeaveBalanceSummary balance={me.data.balance} />
          <div className="team-actions">
            {me.data.employee.email.includes('@') && (
              <button type="button" onClick={() => emailBalance.mutate()}>Email my balance</button>
            )}
            <button type="button" onClick={() => signOut.mutate()}>Leave this record</button>
          </div>
          {notice && <p role="status">{notice}</p>}
          {formError && <p className="team-error" role="alert">{formError}</p>}
          <form
            className="team-form pin-change"
            onSubmit={(event) => {
              event.preventDefault()
              if (!/^\d{6}$/.test(nextPin)) {
                setFormError('PIN must be 6 digits')
                return
              }
              changePin.mutate()
            }}
          >
            <h3>Change PIN</h3>
            <div className="pin-set">
              <label htmlFor="my-pin">
                New PIN
                <input
                  id="my-pin"
                  inputMode="numeric"
                  autoComplete="off"
                  pattern="\d{6}"
                  minLength={6}
                  maxLength={6}
                  placeholder="6 digits you will remember"
                  value={nextPin}
                  onChange={(event) => setNextPin(event.target.value.replace(/\D/g, '').slice(0, 6))}
                />
              </label>
              <button type="button" onClick={() => setNextPin(generatePin())}>Generate</button>
            </div>
            <button className="primary" type="submit">Save PIN</button>
          </form>
          <h3>Your requests</h3>
          {(me.data.requests || []).length === 0 && <p className="team-muted">You have no leave requests.</p>}
          <ul className="team-list my-requests">
            {[...me.data.requests].sort((a, b) => b.startDate.localeCompare(a.startDate)).map((request) => (
              <li key={request.id} className={`my-request is-${request.type}`}>
                <strong>{leaveTypeLabel(request.type)}</strong>
                <span className="my-request-dates">{requestDateLabel(request)}</span>
                <span className={`leave-status is-${request.status}`}>{requestStatusLabel(request.status)}</span>
                {request.decisionNote && <p className="my-request-note">Note: {request.decisionNote}</p>}
                {canCancelRequest(request, leaveToday()) && (
                  <button
                    type="button"
                    onClick={() => {
                      if (request.status === 'approved' && !window.confirm('Cancel this approved leave? It will come off the team calendar.')) return
                      cancelRequest.mutate(request.id)
                    }}
                  >
                    Cancel
                  </button>
                )}
              </li>
            ))}
          </ul>
          <form className="team-form" onSubmit={onRequest}>
            <h3>Request leave</h3>
            <label htmlFor="leave-type">
              Type
              <select id="leave-type" name="type" value={requestType} onChange={(event) => setRequestType(formLeaveType(event.target.value))}>
                <option value="annual">Annual leave</option>
                <option value="sick">Sick leave</option>
                <option value="study">Study leave</option>
                <option value="unpaid">Unpaid leave</option>
              </select>
            </label>
            <label htmlFor="leave-start">
              First day
              <input id="leave-start" name="startDate" type="date" required value={requestStart} onChange={(event) => setRequestStart(event.target.value)} />
            </label>
            <label htmlFor="leave-end">
              Last day
              <input id="leave-end" name="endDate" type="date" required value={requestEnd} onChange={(event) => setRequestEnd(event.target.value)} />
            </label>
            <label className="team-check" htmlFor="leave-start-half">
              <span>Half day on the first day</span>
              <input id="leave-start-half" name="startHalf" type="checkbox" checked={requestStartHalf} onChange={(event) => setRequestStartHalf(event.target.checked)} />
            </label>
            <label className="team-check" htmlFor="leave-end-half">
              <span>Half day on the last day, when the request covers more than one day</span>
              <input id="leave-end-half" name="endHalf" type="checkbox" checked={requestEndHalf} onChange={(event) => setRequestEndHalf(event.target.checked)} />
            </label>
            <p className="team-muted request-note">Only ordinary working days are counted. Public holidays, weekends, and other days off are left out. For annual leave, a shorter day counts as part of a full day. Extra time off can be requested as unpaid leave.</p>
            {requestWarning && <p className="team-error" role="alert">{requestWarning}</p>}
            <label htmlFor="leave-note">
              Note
              <textarea id="leave-note" name="note" rows={2} />
            </label>
            <button className="primary" type="submit" disabled={Boolean(requestWarning)}>Submit request</button>
          </form>
        </div>
      )}
    </section>
  )
}

function EmployeeAdmin() {
  const queryClient = useQueryClient()
  const employees = useQuery({ queryKey: ['leave-employees'], queryFn: leaveService.getEmployees })
  const [error, setError] = useState('')
  const [saved, setSaved] = useState('')
  const [filter, setFilter] = useState('')
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)
  const [addVersion, setAddVersion] = useState(0)
  const [chosenPin, setChosenPin] = useState('')
  const [createPin, setCreatePin] = useState('')
  const [newPin, setNewPin] = useState<{ name: string; pin: string; emailed: boolean } | null>(null)
  const [annualWeeks, setAnnualWeeks] = useState('4')

  useEffect(() => {
    setChosenPin('')
  }, [selectedId])

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['leave-employees'] })
  const list = employees.data || []
  const visible = list.filter((employee) => {
    const haystack = `${employee.name} ${employee.email}`.toLowerCase()
    return haystack.includes(filter.trim().toLowerCase())
  })
  const selected = list.find((employee) => employee.id === selectedId) || null

  useEffect(() => {
    if (adding) {
      setAnnualWeeks('4')
      return
    }
    if (selected) setAnnualWeeks(String(Math.round(annualLeaveWeeksOf(selected) * 100) / 100))
  }, [adding, selected])

  const create = useMutation({
    mutationFn: leaveService.createEmployee,
    onSuccess: async (employee, input) => {
      setError('')
      setSaved(`${employee.name} added.`)
      setNewPin({ name: employee.name, pin: input.pin, emailed: input.email.includes('@') })
      setCreatePin('')
      setAdding(false)
      setSelectedId(employee.id)
      await refresh()
      await queryClient.invalidateQueries({ queryKey: ['leave-calendar'] })
      await queryClient.invalidateQueries({ queryKey: ['leave-outbox'] })
    },
    onError: (err) => setError(messageFrom(err)),
  })
  const update = useMutation({
    mutationFn: ({ id, input }: { id: string; input: Partial<LeaveEmployeeInput> }) => leaveService.updateEmployee(id, input),
    onSuccess: async (employee, variables) => {
      setError('')
      setSaved(`${employee.name} saved.`)
      if (variables.input.pin) {
        setNewPin({ name: employee.name, pin: variables.input.pin, emailed: false })
        setChosenPin('')
      }
      await refresh()
      await queryClient.invalidateQueries({ queryKey: ['leave-calendar'] })
    },
    onError: (err) => setError(messageFrom(err)),
  })

  const onCreate = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const formElement = event.currentTarget
    const form = new FormData(formElement)
    const pin = createPin.trim()
    if (!/^\d{6}$/.test(pin)) {
      setError('PIN must be 6 digits')
      return
    }
    if (!Number.isFinite(Number(annualWeeks)) || Number(annualWeeks) < 0) {
      setError('Annual leave weeks cannot be negative')
      return
    }
    let pattern: ReturnType<typeof workingPatternFromInput>
    try {
      pattern = workingPatternFromInput({ dailyHours: dailyHoursFromForm(form) })
    } catch (err) {
      setError(messageFrom(err))
      return
    }
    create.mutate({
      name: String(form.get('name') || ''),
      email: String(form.get('email') || ''),
      ...pattern,
      annualLeaveWeeks: Number(annualWeeks),
      studyLeaveDays: Number(form.get('studyLeaveDays')),
      startDate: String(form.get('startDate') || ''),
      pin,
      ...(String(form.get('annualBalance') || '').trim() ? { annualBalance: Number(form.get('annualBalance')) } : {}),
      ...(String(form.get('sickBalance') || '').trim() ? { sickBalance: Number(form.get('sickBalance')) } : {}),
    }, {
      onSuccess: () => {
        formElement.reset()
        setAddVersion((version) => version + 1)
      },
    })
  }

  const onEdit = (event: FormEvent<HTMLFormElement>, employee: LeaveEmployeeWithBalance) => {
    event.preventDefault()
    const form = new FormData(event.currentTarget)
    const pin = String(form.get('pin') || '').trim()
    if (pin && !/^\d{6}$/.test(pin)) {
      setError('PIN must be 6 digits')
      return
    }
    if (!Number.isFinite(Number(annualWeeks)) || Number(annualWeeks) < 0) {
      setError('Annual leave weeks cannot be negative')
      return
    }
    let pattern: ReturnType<typeof workingPatternFromInput>
    try {
      pattern = workingPatternFromInput({ dailyHours: dailyHoursFromForm(form) })
    } catch (err) {
      setError(messageFrom(err))
      return
    }
    update.mutate({
      id: employee.id,
      input: {
        name: String(form.get('name') || ''),
        email: String(form.get('email') || ''),
        ...pattern,
        annualLeaveWeeks: Number(annualWeeks),
        studyLeaveDays: Number(form.get('studyLeaveDays')),
        startDate: String(form.get('startDate') || ''),
        active: form.get('active') === 'on',
        ...(pin ? { pin } : {}),
        annualBalance: form.get('calculateAnnual') === 'on' ? null : Number(form.get('annualBalance')),
        annualAccrualFrozen: form.get('calculateAnnual') === 'on' ? false : form.get('freezeAnnual') === 'on',
        sickBalance: form.get('calculateSick') === 'on' ? null : Number(form.get('sickBalance')),
      },
    })
  }

  return (
    <div className="employee-admin">
      <div className="employee-directory">
        <div className="employee-toolbar">
          <label className="employee-search" htmlFor="employee-filter">
            Find an employee
            <input
              id="employee-filter"
              value={filter}
              onChange={(event) => setFilter(event.target.value)}
              placeholder="Name or email"
            />
          </label>
          <button
            type="button"
            className="primary"
            onClick={() => {
              setAdding(true)
              setSelectedId(null)
              setSaved('')
              setNewPin(null)
              setCreatePin('')
            }}
          >
            Add employee
          </button>
        </div>
        <div className="employee-table-scroll">
          <table className="employee-table">
            <caption className="team-muted">{visible.length} of {list.length} employees</caption>
            <thead>
              <tr>
                <th scope="col">Name</th>
                <th scope="col">Works</th>
                <th scope="col">Annual days</th>
                <th scope="col">Study days</th>
                <th scope="col">Balance</th>
                <th scope="col">Status</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((employee) => (
                <tr key={employee.id} className={employee.id === selectedId ? 'is-selected' : undefined}>
                  <th scope="row">
                    <button
                      type="button"
                      className="team-text-button"
                      aria-current={employee.id === selectedId ? 'true' : undefined}
                      onClick={() => {
                        setSelectedId(employee.id)
                        setAdding(false)
                        setSaved('')
                        setNewPin(null)
                      }}
                    >
                      {employee.name}
                      {employee.email ? <span>{employee.email}</span> : null}
                    </button>
                  </th>
                  <td>
                    <span className="works-days">{workingDayNames(normalizeWorkingDays(employee.workingDays)).replace('Monday to Friday', 'Mon–Fri')}</span>
                    <span className="works-hours">{worksHoursLabel(employee)}</span>
                  </td>
                  <td>{employee.annualLeaveDays}</td>
                  <td>{employee.studyLeaveDays || 0}</td>
                  <td>{formatDays(employee.balance.annualAvailable)}</td>
                  <td>{employee.active ? 'Active' : 'Inactive'}</td>
                </tr>
              ))}
              {visible.length === 0 && (
                <tr>
                  <td colSpan={6}>No employees match that search.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <section className="employee-editor" aria-live="polite">
        {error && <p className="team-error" role="alert">{error}</p>}
        {saved && !adding && <p role="status">{saved}</p>}
        {newPin && (
          <p className="pin-reveal" role="status">
            PIN for {newPin.name}: {newPin.pin}. It is saved on their record and shown once.{newPin.emailed ? ' It has been emailed to them.' : ''}
          </p>
        )}
        {adding && (
          <form key={addVersion} className="team-form employee-editor-form" onSubmit={onCreate}>
            <h3>Add employee</h3>
            <label htmlFor="emp-name">Name<input id="emp-name" name="name" required /></label>
            <label htmlFor="emp-email">Email<input id="emp-email" name="email" type="email" placeholder="Optional" /></label>
            <label htmlFor="emp-weeks">
              Annual leave weeks
              <input id="emp-weeks" type="number" min="0" step="0.5" value={annualWeeks} onChange={(event) => setAnnualWeeks(event.target.value)} required />
            </label>
            <label htmlFor="emp-study-days">Study days<input id="emp-study-days" name="studyLeaveDays" type="number" min="0" step="0.5" defaultValue={0} required /></label>
            <label htmlFor="emp-start">Start date<input id="emp-start" name="startDate" type="date" required /></label>
            <WorkingPatternFields idPrefix="emp" dailyHours={DEFAULT_DAILY_HOURS} weeks={annualWeeks} />
            <label htmlFor="emp-annual-balance">
              Annual leave balance
              <input id="emp-annual-balance" name="annualBalance" type="number" min="0" max="365" step="0.5" />
            </label>
            <label htmlFor="emp-sick-balance">
              Sick leave balance
              <input id="emp-sick-balance" name="sickBalance" type="number" min="0" max="365" step="0.5" />
            </label>
            <p className="team-muted employee-accrued">Days remaining today. Leave blank to calculate annual leave from the contract weeks and start date, and sick leave from the statutory allowance. A balance you type still increases through the rest of the year. Study days are a fixed allowance for each year from the start date. They are not accrued, and unused days do not carry over.</p>
            <div className="pin-set">
              <label htmlFor="emp-pin">
                6-digit PIN
                <input
                  id="emp-pin"
                  name="pin"
                  inputMode="numeric"
                  autoComplete="off"
                  pattern="\d{6}"
                  minLength={6}
                  maxLength={6}
                  placeholder="6 digits they will remember"
                  value={createPin}
                  onChange={(event) => setCreatePin(event.target.value.replace(/\D/g, '').slice(0, 6))}
                  required
                />
              </label>
              <button type="button" onClick={() => setCreatePin(generatePin())}>Generate</button>
            </div>
            <div className="team-actions">
              <button className="primary" type="submit">Save employee</button>
              <button type="button" onClick={() => setAdding(false)}>Cancel</button>
            </div>
          </form>
        )}
        {!adding && selected && (
          <form key={`${selected.id}-${selected.balance.annualAccrualFrozen ? 'frozen' : 'accruing'}`} className="team-form employee-editor-form" onSubmit={(event) => onEdit(event, selected)}>
            <h3>Edit {selected.name}</h3>
            <p className="employee-accrued">Accrued annual leave: {formatDays(selected.balance.annualAccrued)} days as of {formatDisplayDate(selected.balance.asOf)}. The contract is {formatDays(annualLeaveWeeksOf(selected))} weeks, {formatDays(selected.annualLeaveDays)} days a year for this pattern. {selected.balance.annualAccrualFrozen ? 'Annual leave will not accrue any further until 1 January. The balance above is what remains.' : 'A typed balance is days remaining today, and it keeps increasing through the rest of this year. Tick No further accrual this year when leave was taken without being recorded.'} Study leave is {formatDays(selected.balance.studyAvailable)} of {formatDays(selected.balance.studyEntitlement)} days for {formatDisplayDate(selected.balance.studyYearStart)} – {formatDisplayDate(selected.balance.studyYearEnd)}. It is fixed from the start date, not accrued, and unused days do not carry over.</p>
            <label htmlFor="edit-name">Name<input id="edit-name" name="name" defaultValue={selected.name} required /></label>
            <label htmlFor="edit-email">Email<input id="edit-email" name="email" type="email" defaultValue={selected.email} placeholder="Optional" /></label>
            <label htmlFor="edit-weeks">
              Annual leave weeks
              <input id="edit-weeks" type="number" min="0" step="0.5" value={annualWeeks} onChange={(event) => setAnnualWeeks(event.target.value)} required />
            </label>
            <label htmlFor="edit-study-days">Study days<input id="edit-study-days" name="studyLeaveDays" type="number" min="0" step="0.5" defaultValue={selected.studyLeaveDays || 0} required /></label>
            <label htmlFor="edit-start">Start date<input id="edit-start" name="startDate" type="date" defaultValue={selected.startDate} required /></label>
            <WorkingPatternFields idPrefix="edit" dailyHours={dailyHoursOf(selected)} weeks={annualWeeks} />
            <label htmlFor="edit-annual-balance">
              Annual leave balance
              <input id="edit-annual-balance" name="annualBalance" type="number" min="0" max="365" step="0.01" defaultValue={(Math.round(selected.balance.annualAvailable * 100) / 100).toFixed(2)} required />
            </label>
            <label htmlFor="edit-sick-balance">
              Sick leave balance
              <input id="edit-sick-balance" name="sickBalance" type="number" min="0" max="365" step="0.01" defaultValue={(Math.round(selected.balance.sickAvailable * 100) / 100).toFixed(2)} required />
            </label>
            <div className="editor-options">
              <label className="team-check" htmlFor="edit-freeze-annual">
                <span>No further accrual this year</span>
                <input id="edit-freeze-annual" name="freezeAnnual" type="checkbox" defaultChecked={selected.balance.annualAccrualFrozen === true} />
              </label>
              <label className="team-check" htmlFor="edit-calculate-annual">
                <span>Calculate annual leave again</span>
                <input id="edit-calculate-annual" name="calculateAnnual" type="checkbox" />
              </label>
              <label className="team-check" htmlFor="edit-calculate-sick">
                <span>Calculate sick leave again</span>
                <input id="edit-calculate-sick" name="calculateSick" type="checkbox" />
              </label>
              <label className="team-check" htmlFor="edit-active">
                <span>Active</span>
                <input id="edit-active" name="active" type="checkbox" defaultChecked={selected.active} />
              </label>
            </div>
            <div className="pin-set">
              <label htmlFor="edit-pin">
                New PIN
                <input
                  id="edit-pin"
                  name="pin"
                  inputMode="numeric"
                  autoComplete="off"
                  pattern="\d{6}"
                  minLength={6}
                  maxLength={6}
                  placeholder="6 digits they will remember"
                  value={chosenPin}
                  onChange={(event) => setChosenPin(event.target.value.replace(/\D/g, '').slice(0, 6))}
                />
              </label>
              <button type="button" onClick={() => setChosenPin(generatePin())}>Generate</button>
            </div>
            <p className="team-muted employee-accrued">Leave the PIN blank to keep the current one. A new PIN is saved when you save, and shown once.</p>
            <div className="team-actions">
              <button className="primary" type="submit">Save changes</button>
            </div>
          </form>
        )}
        {!adding && !selected && (
          <p className="team-note">Select an employee to edit their record, or add someone new. The list stays in view while you work.</p>
        )}
      </section>
    </div>
  )
}

type AdminTab = 'employees' | 'requests' | 'pay' | 'emails' | 'settings'

function AdminWorkspace() {
  const [tab, setTab] = useState<AdminTab>('employees')
  const tabs: Array<{ id: AdminTab; label: string }> = [
    { id: 'employees', label: 'Employees' },
    { id: 'requests', label: 'Requests' },
    { id: 'pay', label: 'Pay report' },
    { id: 'emails', label: 'Emails' },
    { id: 'settings', label: 'Settings' },
  ]

  return (
    <section className="team-panel team-admin" aria-labelledby="team-admin-heading">
      <div className="team-admin-header">
        <h2 id="team-admin-heading">Administration</h2>
        <div className="team-tabs" role="tablist" aria-label="Administration">
          {tabs.map((item) => (
            <button
              key={item.id}
              type="button"
              role="tab"
              id={`team-tab-${item.id}`}
              aria-selected={tab === item.id}
              aria-controls={`team-panel-${item.id}`}
              className={tab === item.id ? 'is-selected' : undefined}
              onClick={() => setTab(item.id)}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>
      <div role="tabpanel" id={`team-panel-${tab}`} aria-labelledby={`team-tab-${tab}`}>
        {tab === 'employees' && <EmployeeAdmin />}
        {tab === 'requests' && <RequestAdmin />}
        {tab === 'pay' && <PayReportPanel />}
        {tab === 'emails' && <OutboxPanel />}
        {tab === 'settings' && <SettingsPanel />}
      </div>
    </section>
  )
}

function RequestAdmin() {
  const queryClient = useQueryClient()
  const requests = useQuery({ queryKey: ['leave-requests'], queryFn: leaveService.getRequests })
  const employees = useQuery({ queryKey: ['leave-employees'], queryFn: leaveService.getEmployees })
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [decliningId, setDecliningId] = useState<string | null>(null)
  const [declineNote, setDeclineNote] = useState('')
  const [entryEmployeeId, setEntryEmployeeId] = useState('')
  const [entryType, setEntryType] = useState<LeaveType>('annual')
  const [entryStart, setEntryStart] = useState('')
  const [entryEnd, setEntryEnd] = useState('')
  const [entryStartHalf, setEntryStartHalf] = useState(false)
  const [entryEndHalf, setEntryEndHalf] = useState(false)
  const enterRequest = useMutation({
    mutationFn: leaveService.createRequestForEmployee,
    onSuccess: async (request) => {
      setError('')
      setNotice(`${leaveTypeLabel(request.type)} for ${request.employeeName} is waiting for approval.`)
      await queryClient.invalidateQueries({ queryKey: ['leave-requests'] })
      await queryClient.invalidateQueries({ queryKey: ['leave-me'] })
    },
    onError: (err) => {
      setNotice('')
      setError(messageFrom(err))
    },
  })
  const entryEmployee = (employees.data || []).find((employee) => employee.id === entryEmployeeId)
  const entryDraft = {
    id: 'draft',
    type: entryType,
    startDate: entryStart,
    endDate: entryEnd,
    startPortion: (entryStartHalf ? 0.5 : 1) as DayPortion,
    endPortion: ((entryStart === entryEnd ? entryStartHalf : entryEndHalf) ? 0.5 : 1) as DayPortion,
  }
  const entryWarning = annualRequestWarning(entryEmployee, requests.data || [], entryDraft)
  const onEnterRequest = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (entryWarning) {
      setNotice('')
      setError(entryWarning)
      return
    }
    const form = event.currentTarget
    const data = new FormData(form)
    enterRequest.mutate({
      employeeId: entryEmployeeId,
      type: entryType,
      startDate: entryStart,
      endDate: entryEnd,
      startPortion: entryDraft.startPortion,
      endPortion: entryDraft.endPortion,
      note: String(data.get('note') || ''),
    }, {
      onSuccess: () => {
        form.reset()
        setEntryEmployeeId('')
        setEntryType('annual')
        setEntryStart('')
        setEntryEnd('')
        setEntryStartHalf(false)
        setEntryEndHalf(false)
      },
    })
  }
  const review = useMutation({
    mutationFn: async (input: { id: string; action: 'approve' | 'decline'; decisionNote?: string }) => {
      if (input.action === 'decline') return leaveService.declineRequest(input.id, input.decisionNote)
      return leaveService.approveRequest(input.id)
    },
    onSuccess: async () => {
      setError('')
      setDecliningId(null)
      setDeclineNote('')
      await queryClient.invalidateQueries({ queryKey: ['leave-requests'] })
      await queryClient.invalidateQueries({ queryKey: ['leave-calendar'] })
      await queryClient.invalidateQueries({ queryKey: ['leave-pay-report'] })
      await queryClient.invalidateQueries({ queryKey: ['leave-me'] })
      await queryClient.invalidateQueries({ queryKey: ['leave-outbox'] })
    },
    onError: (err) => setError(messageFrom(err)),
  })
  const pending = (requests.data || [])
    .filter((request) => request.status === 'pending')
    .sort((a, b) => a.employeeName.localeCompare(b.employeeName, 'en', { sensitivity: 'base' }) || a.startDate.localeCompare(b.startDate))

  const activeEmployees = (employees.data || []).filter((employee) => employee.active)

  return (
    <div className="request-admin">
      <div className="request-queue">
        <h3>Waiting for a decision</h3>
        {notice && <p role="status">{notice}</p>}
        {error && <p className="team-error" role="alert">{error}</p>}
        {pending.length === 0 && <p className="team-muted">No requests waiting for a decision.</p>}
        <ul className="team-list">
          {pending.map((request: LeaveRequest) => (
            <li key={request.id} className={`request-row is-${request.type}`}>
              <div>
                <strong>{request.employeeName}</strong>
                <div className="team-muted">
                  {leaveTypeLabel(request.type)}, {formatDisplayDate(request.startDate)} to {formatDisplayDate(request.endDate)}
                  {request.enteredByAdmin ? ' · Entered by an admin' : ''}
                </div>
                {request.exceedsAccrual && (
                  <p className="team-error" role="alert">
                    {annualRequestWarning(
                      (employees.data || []).find((employee) => employee.id === request.employeeId),
                      requests.data || [],
                      request,
                    ) || 'You do not have enough leave for this period.'}
                  </p>
                )}
              </div>
              <div className="team-actions">
                <button type="button" className="primary" disabled={request.exceedsAccrual === true} onClick={() => review.mutate({ id: request.id, action: 'approve' })}>Approve</button>
                {decliningId !== request.id && (
                  <button
                    type="button"
                    onClick={() => {
                      setDecliningId(request.id)
                      setDeclineNote('')
                    }}
                  >
                    Decline
                  </button>
                )}
              </div>
              {decliningId === request.id && (
                <form
                  className="decline-form request-decline"
                  onSubmit={(event) => {
                    event.preventDefault()
                    review.mutate({ id: request.id, action: 'decline', decisionNote: declineNote })
                  }}
                >
                  <label htmlFor={`decline-note-${request.id}`}>
                    Note for {request.employeeName}, optional
                    <textarea
                      id={`decline-note-${request.id}`}
                      rows={2}
                      value={declineNote}
                      onChange={(event) => setDeclineNote(event.target.value)}
                    />
                  </label>
                  <div className="team-actions">
                    <button type="submit">Decline request</button>
                    <button type="button" onClick={() => { setDecliningId(null); setDeclineNote('') }}>Back</button>
                  </div>
                </form>
              )}
            </li>
          ))}
        </ul>
      </div>
      <form className="team-form request-entry-form" onSubmit={onEnterRequest}>
        <h3>Enter a request for an employee</h3>
        <p className="team-muted">For a day that was missed. It still waits for approval, then joins the pay report.</p>
        <label htmlFor="admin-request-employee">
          Employee
          <select id="admin-request-employee" name="employeeId" required value={entryEmployeeId} onChange={(event) => setEntryEmployeeId(event.target.value)}>
            <option value="" disabled>Choose an employee</option>
            {activeEmployees.map((employee) => (
              <option key={employee.id} value={employee.id}>{employee.name}</option>
            ))}
          </select>
        </label>
        <label htmlFor="admin-request-type">
          Type
          <select id="admin-request-type" name="type" value={entryType} onChange={(event) => setEntryType(formLeaveType(event.target.value))}>
            <option value="annual">Annual leave</option>
            <option value="sick">Sick leave</option>
            <option value="study">Study leave</option>
            <option value="unpaid">Unpaid leave</option>
          </select>
        </label>
        <label htmlFor="admin-request-start">
          First day
          <input id="admin-request-start" name="startDate" type="date" required value={entryStart} onChange={(event) => setEntryStart(event.target.value)} />
        </label>
        <label htmlFor="admin-request-end">
          Last day
          <input id="admin-request-end" name="endDate" type="date" required value={entryEnd} onChange={(event) => setEntryEnd(event.target.value)} />
        </label>
        <label className="team-check" htmlFor="admin-request-start-half">
          <span>Half day on the first day</span>
          <input id="admin-request-start-half" name="startHalf" type="checkbox" checked={entryStartHalf} onChange={(event) => setEntryStartHalf(event.target.checked)} />
        </label>
        <label className="team-check" htmlFor="admin-request-end-half">
          <span>Half day on the last day</span>
          <input id="admin-request-end-half" name="endHalf" type="checkbox" checked={entryEndHalf} onChange={(event) => setEntryEndHalf(event.target.checked)} />
        </label>
        <p className="team-muted request-note">Only that employee’s ordinary working days are counted. Public holidays, weekends, and other days off are left out. For annual leave, a shorter day counts as part of a full day. Extra time off can be entered as unpaid leave.</p>
        {entryWarning && <p className="team-error" role="alert">{entryWarning}</p>}
        <label className="request-note" htmlFor="admin-request-note">
          Note, optional
          <input id="admin-request-note" name="note" type="text" />
        </label>
        <button className="primary" type="submit" disabled={Boolean(entryWarning)}>Submit for approval</button>
      </form>
    </div>
  )
}

function PayReportPanel() {
  const queryClient = useQueryClient()
  const report = useQuery({ queryKey: ['leave-pay-report'], queryFn: leaveService.getPayReport })
  const employees = useQuery({ queryKey: ['leave-employees'], queryFn: leaveService.getEmployees })
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const approve = useMutation({
    mutationFn: () => leaveService.approvePayReport(),
    onSuccess: async () => {
      setError('')
      setNotice('Sent to the accountant, with the admins copied.')
      await queryClient.invalidateQueries({ queryKey: ['leave-pay-report'] })
      await queryClient.invalidateQueries({ queryKey: ['leave-outbox'] })
    },
    onError: (err) => setError(messageFrom(err)),
  })
  const sendAdmins = useMutation({
    mutationFn: () => leaveService.sendAdminReport(),
    onSuccess: async () => {
      setError('')
      setNotice('The report was emailed to the admins.')
      await queryClient.invalidateQueries({ queryKey: ['leave-outbox'] })
    },
    onError: (err) => setError(messageFrom(err)),
  })
  const addHours = useMutation({
    mutationFn: leaveService.addAdditionalHours,
    onSuccess: async (entry) => {
      setError('')
      const current = report.data
      const inPeriod = current && entry.date >= current.periodStart && entry.date <= current.periodEnd
      setNotice(inPeriod
        ? `${entry.employeeName}: ${formatHours(entry.hours)} hours on ${formatDisplayDate(entry.date)} added to this pay report.`
        : `Recorded for ${formatDisplayDate(entry.date)}. It will appear on the pay report for the period that includes that date.`)
      await queryClient.invalidateQueries({ queryKey: ['leave-pay-report'] })
    },
    onError: (err) => {
      setNotice('')
      setError(messageFrom(err))
    },
  })
  const removeHours = useMutation({
    mutationFn: leaveService.removeAdditionalHours,
    onSuccess: async () => {
      setError('')
      setNotice('Additional hours removed.')
      await queryClient.invalidateQueries({ queryKey: ['leave-pay-report'] })
    },
    onError: (err) => setError(messageFrom(err)),
  })
  const data = report.data
  const activeEmployees = (employees.data || []).filter((employee) => employee.active)
  const onAddHours = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const form = event.currentTarget
    const formData = new FormData(form)
    addHours.mutate({
      employeeId: String(formData.get('employeeId') || ''),
      date: String(formData.get('date') || ''),
      hours: Number(formData.get('hours')),
      note: String(formData.get('note') || ''),
    }, {
      onSuccess: (entry) => {
        form.reset()
        queryClient.setQueryData<PayReport>(['leave-pay-report'], (current) => {
          if (!current || entry.date < current.periodStart || entry.date > current.periodEnd) return current
          if (current.additionalHours.some((item) => item.id === entry.id)) return current
          return { ...current, additionalHours: [...current.additionalHours, entry] }
        })
      },
    })
  }

  return (
    <div>
      {report.isLoading && <p>Loading the pay report…</p>}
      {notice && <p role="status">{notice}</p>}
      {error && <p className="team-error" role="alert">{error}</p>}
      {data && (
        <>
          <div className="pay-report-toolbar">
            {data.status === 'scheduled' && (
              <p className="team-note">You can send this report now. If it is still open on {formatDisplayDate(data.dueDate)}, admins are emailed automatically.</p>
            )}
            {data.status === 'pending_approval' && <p className="team-note">Waiting for an admin to approve. The accountant has not been emailed yet.</p>}
            {data.status === 'sent' && <p role="status">Sent to the accountant, with the admins copied.</p>}
            <button type="button" onClick={() => sendAdmins.mutate()} disabled={sendAdmins.isPending}>Send to admins</button>
            {data.status !== 'sent' && (
              <button className="primary" type="button" onClick={() => approve.mutate()} disabled={approve.isPending}>Approve and send</button>
            )}
          </div>
          <PayReportDocument
            payday={data.payday}
            periodStart={data.periodStart}
            periodEnd={data.periodEnd}
            lines={data.lines}
            additionalHours={data.additionalHours}
          />
          {data.status !== 'sent' && (
            <form className="team-form hours-form" onSubmit={onAddHours}>
              <h3>Additional hours worked</h3>
              <p className="team-muted hours-form-intro">
                Record extra time on a date, for example a day covering reception. Hours dated {formatDisplayDate(data.periodStart)} to {formatDisplayDate(data.periodEnd)} are included in this report. A period already sent to the accountant cannot be changed.
              </p>
              <label htmlFor="extra-hours-employee">
                Employee
                <select id="extra-hours-employee" name="employeeId" required defaultValue="">
                  <option value="" disabled>Choose an employee</option>
                  {activeEmployees.map((employee) => (
                    <option key={employee.id} value={employee.id}>{employee.name}</option>
                  ))}
                </select>
              </label>
              <label htmlFor="extra-hours-date">
                Date
                <input
                  id="extra-hours-date"
                  name="date"
                  type="date"
                  required
                  min={data.periodStart}
                  max={data.periodEnd}
                  defaultValue={leaveToday() >= data.periodStart && leaveToday() <= data.periodEnd ? leaveToday() : data.periodEnd}
                />
              </label>
              <label htmlFor="extra-hours-amount">
                Hours
                <input id="extra-hours-amount" name="hours" type="number" min="0.5" max="24" step="0.5" required />
              </label>
              <label className="hours-note" htmlFor="extra-hours-note">
                Note, optional
                <input id="extra-hours-note" name="note" type="text" />
              </label>
              <div className="hours-form-submit">
                <button className="primary" type="submit">Add hours</button>
              </div>
            </form>
          )}
          {data.status !== 'sent' && data.additionalHours.length > 0 && (
            <ul className="team-list hours-entries">
              {data.additionalHours.map((item) => (
                <li key={item.id} className="hours-entry">
                  <div>
                    <strong>{item.employeeName}</strong>
                    <div>{formatDisplayDate(item.date)}, {formatHours(item.hours)} hours{item.note ? ` — ${item.note}` : ''}</div>
                  </div>
                  <button type="button" onClick={() => removeHours.mutate(item.id)}>Remove</button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  )
}

function recipientLabel(email: LeaveEmail): string {
  const first = email.to[0] || ''
  const more = email.to.length + email.cc.length - 1
  if (!first) return 'No recipient'
  return more > 0 ? `${first} +${more}` : first
}

function OutboxPanel() {
  const outbox = useQuery({ queryKey: ['leave-outbox'], queryFn: leaveService.getOutbox })
  const [openId, setOpenId] = useState<string | null>(null)
  const items = outbox.data || []
  return (
    <div>
      {items.length === 0 && <p className="team-muted">No emails have been recorded yet.</p>}
      <div className="team-list email-list">
        {items.map((email) => {
          const open = openId === email.id
          const detailId = `email-detail-${email.id.replace(/[^a-zA-Z0-9_-]/g, '-')}`
          return (
            <article className="team-email email-row" key={email.id}>
              <button
                type="button"
                className="email-summary"
                aria-expanded={open}
                aria-controls={detailId}
                onClick={() => setOpenId(open ? null : email.id)}
              >
                <time dateTime={email.createdAt}>{formatDisplayDate(email.createdAt.slice(0, 10))}</time>
                <span className="email-subject">{email.subject}</span>
                <span className="email-to">{recipientLabel(email)}</span>
                <span className="email-view">{open ? 'Hide' : 'View'}</span>
              </button>
              {open && (
                <div className="email-detail" id={detailId}>
                  <p className="team-muted">
                    To: {email.to.join(', ') || 'No recipient'}
                    {email.cc.length ? ` · CC: ${email.cc.join(', ')}` : ''}
                  </p>
                  {email.report ? (
                    <PayReportDocument
                      payday={email.report.payday}
                      periodStart={email.report.periodStart}
                      periodEnd={email.report.periodEnd}
                      lines={email.report.lines}
                      additionalHours={email.report.additionalHours}
                    />
                  ) : (
                    <pre>{email.body}</pre>
                  )}
                </div>
              )}
            </article>
          )
        })}
      </div>
    </div>
  )
}

function SettingsPanel() {
  const queryClient = useQueryClient()
  const settings = useQuery({ queryKey: ['leave-settings'], queryFn: leaveService.getSettings })
  const [error, setError] = useState('')
  const [saved, setSaved] = useState(false)
  const save = useMutation({
    mutationFn: leaveService.updateSettings,
    onSuccess: async () => {
      setError('')
      setSaved(true)
      await queryClient.invalidateQueries({ queryKey: ['leave-settings'] })
    },
    onError: (err) => setError(messageFrom(err)),
  })
  const initial = settings.data
  const emails = useMemo(() => initial?.adminEmails.join(', ') || '', [initial])
  if (!initial) return null

  return (
    <div>
      <form
        className="team-form settings-form"
        onSubmit={(event) => {
          event.preventDefault()
          const form = new FormData(event.currentTarget)
          save.mutate({
            accountantEmail: String(form.get('accountantEmail') || ''),
            adminEmails: String(form.get('adminEmails') || '').split(',').map((email) => email.trim()).filter(Boolean),
            statutorySickDays: Number(form.get('statutorySickDays')),
          })
        }}
      >
        <label htmlFor="accountant-email">
          Accountant email
          <input id="accountant-email" name="accountantEmail" type="email" defaultValue={initial.accountantEmail} required />
        </label>
        <label className="settings-wide" htmlFor="admin-emails">
          Admin emails, separated by commas
          <input id="admin-emails" name="adminEmails" defaultValue={emails} required />
        </label>
        <label htmlFor="sick-days">
          Statutory sick days per year
          <input id="sick-days" name="statutorySickDays" type="number" min="0" step="1" defaultValue={initial.statutorySickDays} required />
        </label>
        {error && <p className="team-error" role="alert">{error}</p>}
        {saved && <p role="status">Settings saved.</p>}
        <button className="primary" type="submit">Save settings</button>
      </form>
    </div>
  )
}

export function TeamPage() {
  const { enabled, isLoading } = useLeaveModuleEnabled()
  const { hasRole, hasPermission } = useAuth()
  const isAdmin = hasRole('admin') || hasPermission('*')

  if (isLoading) return <p className="team-page">Loading Team…</p>
  if (!enabled) return <Navigate to="/dashboard" replace />

  return (
    <div className="team-page">
      <h1>Team</h1>
      <p className="team-intro">See who is on leave, and request your own time off. <Link to="/team/help">How to use Team</Link></p>
      <LeaveCalendar />
      {isAdmin && <AdminWorkspace />}
      <MyLeave />
    </div>
  )
}
